import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from 'context/AuthContext';
import axios from 'axios';

// Argon Dashboard 2 PRO MUI components
import DashboardLayout from 'Layout/DashboardLayout';
import ArgonBox from 'components/ArgonBox';
import ArgonButton from 'components/ArgonButton';
import ArgonTypography from 'components/ArgonTypography';
import SelectOrganization from 'Routes/Organizer/Components/SelectOrganization';

// MUI components
import Card from '@mui/material/Card';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Badge from '@mui/material/Badge';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';

// react-chat-elements components
import { Input, MessageList } from 'react-chat-elements';
import 'react-chat-elements/dist/main.css';

// WebSocket integration
import ChatWebSocket from 'components/ChatSupportWidget/ChatWebSocket';

function ChatAgentTest() {
  // Auth context
  const { idToken, userId, organizations, isAuthenticated } = useAuth();

  // Organization selection
  const [selectedOrganization, setSelectedOrganization] = useState(null);

  // Conversations state
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [conversationsLoading, setConversationsLoading] = useState(false);

  // Messages state
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const messageListRef = useRef(null);

  // WebSocket state
  const [wsConnected, setWsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState(new Set());
  const wsRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages]);

  // Initialize WebSocket when organization and auth are ready
  useEffect(() => {
    if (
      selectedOrganization &&
      selectedOrganization.value &&
      isAuthenticated &&
      idToken &&
      userId &&
      !wsRef.current
    ) {
      initializeWebSocket();
    } else if (!selectedOrganization && wsRef.current) {
      // Disconnect WebSocket when no organization is selected
      wsRef.current.disconnect();
      wsRef.current = null;
      setWsConnected(false);
    }

    return () => {
      // Cleanup on unmount
      if (wsRef.current) {
        wsRef.current.disconnect();
        wsRef.current = null;
      }
    };
  }, [selectedOrganization, isAuthenticated, idToken, userId]);

  // Fetch conversations when organization changes
  useEffect(() => {
    if (
      selectedOrganization &&
      selectedOrganization.value &&
      userId &&
      idToken
    ) {
      fetchAgentConversations(selectedOrganization.value);
    }
  }, [selectedOrganization, userId, idToken]);

  // WebSocket Functions
  const initializeWebSocket = () => {
    if (wsRef.current) return;

    console.log('🔌 Initializing WebSocket for agent chat');
    wsRef.current = new ChatWebSocket('wss://fuq5ko69l8.execute-api.eu-central-1.amazonaws.com/dev', idToken);

    // Set up WebSocket event handlers
    wsRef.current.setMessageHandler(handleWebSocketMessage);
    wsRef.current.setTypingHandler(handleWebSocketTyping);
    wsRef.current.setStatusUpdateHandler(handleWebSocketStatusUpdate);
    wsRef.current.setConnectionChangeHandler(handleWebSocketConnectionChange);
    wsRef.current.setErrorHandler(handleWebSocketError);

    // Connect as agent for the selected organization using v2.0 method
    wsRef.current.connectAsAgent(selectedOrganization.value);
  };

  const handleWebSocketMessage = (messageData) => {
    console.log('📨 Agent received real-time message:', {
      messageData,
      selectedConversationId,
      selectedOrganization: selectedOrganization?.value
    });
    const { conversationId: msgConversationId, message, sender } = messageData;

    // Update conversation list - move conversation to top and update metadata
    setConversations(prev => {
      const updated = prev.map(conv => {
        const convId = conv._id || conv.id;
        if (convId === msgConversationId) {
          return {
            ...conv,
            lastMessageAt: message.createdAt || new Date().toISOString(),
            lastMessage: message.content,
            messageCount: (conv.messageCount || 0) + 1
          };
        }
        return conv;
      });
      
      // Sort by lastMessageAt to move updated conversation to top
      return updated.sort((a, b) => 
        new Date(b.lastMessageAt || b.updatedAt || b.createdAt) - 
        new Date(a.lastMessageAt || a.updatedAt || a.createdAt)
      );
    });

    // If agent is viewing this conversation, add message to chat
    if (selectedConversationId === msgConversationId) {
      const formattedMessage = {
        position: message.senderId === userId ? 'right' : 'left',
        type: 'text',
        text: message.content,
        date: new Date(message.timestamp || message.createdAt),
        title: message.senderId === userId ? 'Agent (You)' : 'User'
      };
      
      setMessages(prev => [...prev, formattedMessage]);
    }
  };

  const handleWebSocketTyping = (typingData) => {
    console.log('⌨️ Agent received typing indicator:', typingData);
    const { conversationId: typingConversationId, senderId, senderType, isTyping } = typingData;

    // Only show typing indicators for current conversation and from users (not other agents)
    if (selectedConversationId === typingConversationId && senderId !== userId) {
      setTypingUsers(prev => {
        const newTypingUsers = new Set(prev);
        if (isTyping) {
          newTypingUsers.add(senderId);
        } else {
          newTypingUsers.delete(senderId);
        }
        return newTypingUsers;
      });

      // Auto-clear typing indicator after 3 seconds
      if (isTyping) {
        setTimeout(() => {
          setTypingUsers(prev => {
            const newTypingUsers = new Set(prev);
            newTypingUsers.delete(senderId);
            return newTypingUsers;
          });
        }, 3000);
      }
    }
  };

  const handleWebSocketStatusUpdate = (statusData) => {
    console.log('📊 Agent received status update:', statusData);
    const { conversationId: statusConversationId, field, value } = statusData;

    // Update conversation status in local state
    setConversations(prev => 
      prev.map(conv => {
        const convId = conv._id || conv.id;
        return convId === statusConversationId ? { ...conv, [field]: value } : conv;
      })
    );
  };

  const handleWebSocketConnectionChange = (connected, shouldReconnect = false) => {
    console.log('🔌 Agent WebSocket connection changed:', { connected, shouldReconnect });
    setWsConnected(connected);

    if (shouldReconnect && !connected) {
      // Attempt to reinitialize WebSocket after a delay
      setTimeout(() => {
        if (selectedOrganization && isAuthenticated && idToken && userId) {
          console.log('🔄 Agent attempting to reconnect WebSocket...');
          initializeWebSocket();
        }
      }, 2000);
    }
  };

  const handleWebSocketError = (error) => {
    console.error('❌ Agent WebSocket error:', error);
    setWsConnected(false);
  };

  // API Functions
  const fetchAgentConversations = async (organizationId) => {
    if (!idToken || !userId) return;

    try {
      setConversationsLoading(true);
      console.log('Fetching agent conversations for org:', organizationId);

      const response = await axios.get(
        `https://fuq5ko69l8.execute-api.eu-central-1.amazonaws.com/dev/agents/conversations`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          params: {
            organizationId: organizationId,
            limit: 50,
            page: 1
          }
        }
      );

      console.log('Agent conversations response:', response.data);
      const fetchedConversations = response.data.data || response.data || [];
      setConversations(fetchedConversations);

      // Clear selected conversation when switching organizations
      setSelectedConversationId(null);
      setMessages([]);
    } catch (error) {
      console.error('Failed to fetch agent conversations:', error);
      setConversations([]);
    } finally {
      setConversationsLoading(false);
    }
  };

  const loadConversationMessages = async (conversationId) => {
    if (!idToken || !conversationId) return;

    try {
      setMessagesLoading(true);
      console.log('Loading messages for conversation:', conversationId);

      const response = await axios.get(
        `https://fuq5ko69l8.execute-api.eu-central-1.amazonaws.com/dev/agents/conversations/${conversationId}/messages`,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          params: {
            limit: 50,
            page: 1
          }
        }
      );

      console.log('Messages response:', response.data);
      const fetchedMessages = response.data.data || response.data || [];

      // Sort messages by timestamp
      const sortedMessages = fetchedMessages.sort(
        (a, b) =>
          new Date(a.timestamp || a.createdAt) -
          new Date(b.timestamp || b.createdAt)
      );

      // Format messages for react-chat-elements
      const formattedMessages = sortedMessages.map((msg) => ({
        position: msg.senderId === userId ? 'right' : 'left',
        type: 'text',
        text: msg.content,
        date: new Date(msg.timestamp || msg.createdAt),
        title: msg.senderId === userId ? 'Agent (You)' : 'User'
      }));

      setMessages(formattedMessages);
    } catch (error) {
      console.error('Failed to load messages:', error);
      setMessages([]);
    } finally {
      setMessagesLoading(false);
    }
  };

  const createAgentConversation = async (subject = 'Agent Created Conversation', priority = 'medium') => {
    if (!idToken || !selectedOrganization) return null;

    try {
      const response = await axios.post(
        `https://fuq5ko69l8.execute-api.eu-central-1.amazonaws.com/dev/agents/conversations`,
        {
          subject: subject,
          priority: priority,
          organizationId: selectedOrganization.value
          // agentId automatically extracted from JWT
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Agent conversation created:', response.data);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to create agent conversation:', error);
      return null;
    }
  };

  const sendAgentMessage = async () => {
    if (!newMessage.trim() || !selectedConversationId || !idToken) return;

    const messageText = newMessage.trim();
    setNewMessage('');

    // Add message to UI immediately (optimistic update)
    const tempMessage = {
      position: 'right',
      type: 'text',
      text: messageText,
      date: new Date(),
      title: 'Agent (You)'
    };
    setMessages((prev) => [...prev, tempMessage]);

    try {
      setSendingMessage(true);
      console.log('Sending agent message...');
      
      // Try WebSocket first if connected, fallback to REST API
      let messageSent = false;
      if (wsRef.current && wsRef.current.isConnected()) {
        console.log('Attempting to send agent message via WebSocket...');
        messageSent = wsRef.current.sendMessage(selectedConversationId, messageText, 'incoming');
      }
      
      if (!messageSent) {
        console.log('Sending agent message via REST API...');
        const response = await axios.post(
          `https://fuq5ko69l8.execute-api.eu-central-1.amazonaws.com/dev/agents/conversations/${selectedConversationId}/messages`,
          {
            content: messageText
            // senderId automatically extracted from JWT
            // organizationId validated via JWT orga-admin-{organizationId} group
            // type automatically set to 'outgoing' for agents
          },
          {
            headers: {
              Authorization: `Bearer ${idToken}`,
              'Content-Type': 'application/json'
            }
          }
        );
        console.log('Agent message sent via REST API:', response.data);
      } else {
        console.log('Agent message sent successfully via WebSocket');
      }

      // Only reload messages if we used REST API (WebSocket messages come via real-time updates)
      if (!messageSent) {
        await loadConversationMessages(selectedConversationId);
      }
    } catch (error) {
      console.error('Failed to send agent message:', error);
      // Remove the optimistic message on error
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setSendingMessage(false);
    }
  };

  // Event Handlers
  const handleOrganizationChange = (selectedOption) => {
    console.log('Organization changed:', selectedOption);
    setSelectedOrganization(selectedOption);
    // Clear selected conversation when switching organizations
    setSelectedConversationId(null);
    setMessages([]);
  };

  const handleConversationSelect = (conversationId) => {
    setSelectedConversationId(conversationId);
    loadConversationMessages(conversationId);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendAgentMessage();
    }
  };

  const handleTyping = (value) => {
    setNewMessage(value);
    
    // Send typing indicator via WebSocket if connected and we have a conversation
    if (wsRef.current && wsRef.current.isConnected() && selectedConversationId && value.trim()) {
      wsRef.current.sendTyping(selectedConversationId, true);
      
      // Clear any existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Set timeout to stop typing indicator after 1 second of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        if (wsRef.current && wsRef.current.isConnected() && selectedConversationId) {
          wsRef.current.sendTyping(selectedConversationId, false);
        }
      }, 1000);
    }
  };

  // Get selected conversation details
  const selectedConversation = conversations.find(
    (conv) => (conv._id || conv.id) === selectedConversationId
  );

  if (!isAuthenticated) {
    return (
      <DashboardLayout>
        <ArgonBox p={3}>
          <ArgonTypography variant="h4">
            Please log in to access the agent chat.
          </ArgonTypography>
        </ArgonBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <ArgonBox p={3}>
        <ArgonTypography variant="h3" mb={3}>
          Agent Chat Dashboard
        </ArgonTypography>

        {/* Organization Selector */}
        <Card sx={{ mb: 3, p: 2 }}>
          <SelectOrganization
            title="Select Organization"
            size="large"
            onChange={handleOrganizationChange}
            required={true}
            overflow={true}
          />
        </Card>

        {/* Split View Layout */}
        {selectedOrganization && (
          <ArgonBox display="flex" height="70vh" gap={2}>
            {/* Left Panel: Conversations List */}
            <Card
              sx={{
                flex: '0 0 50%',
                display: 'flex',
                flexDirection: 'column'
              }}>
              <ArgonBox p={2} borderBottom="1px solid #e0e0e0">
                <ArgonTypography variant="h6">
                  Conversations ({conversations.length})
                </ArgonTypography>
                {conversationsLoading && (
                  <ArgonTypography variant="caption" color="text">
                    Loading conversations...
                  </ArgonTypography>
                )}
              </ArgonBox>

              <ArgonBox flex={1} sx={{ overflowY: 'auto' }}>
                {conversations.length === 0 && !conversationsLoading ? (
                  <ArgonBox p={3} textAlign="center">
                    <ArgonTypography variant="body2" color="text">
                      No conversations found for this organization.
                    </ArgonTypography>
                  </ArgonBox>
                ) : (
                  <List dense>
                    {conversations.map((conversation, index) => {
                      const convId = conversation._id || conversation.id;
                      const isSelected = convId === selectedConversationId;

                      return (
                        <div key={convId}>
                          <ListItem disablePadding>
                            <ListItemButton
                              selected={isSelected}
                              onClick={() => handleConversationSelect(convId)}
                              sx={{ py: 2 }}>
                              <ListItemText
                                primary={
                                  <ArgonBox
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center">
                                    <ArgonTypography variant="subtitle2">
                                      {conversation.subject ||
                                        `Conversation ${index + 1}`}
                                    </ArgonTypography>
                                    <Badge
                                      badgeContent={conversation.state}
                                      color={
                                        conversation.state === 'open'
                                          ? 'success'
                                          : 'warning'
                                      }
                                      variant="dot"
                                    />
                                  </ArgonBox>
                                }
                                secondary={
                                  <ArgonBox>
                                    <ArgonTypography
                                      variant="caption"
                                      color="text">
                                      {conversation.messageCount || 0} messages
                                    </ArgonTypography>
                                    {conversation.updatedAt && (
                                      <ArgonTypography
                                        variant="caption"
                                        color="text">
                                        {' • '}
                                        {new Date(
                                          conversation.updatedAt
                                        ).toLocaleDateString()}
                                      </ArgonTypography>
                                    )}
                                  </ArgonBox>
                                }
                              />
                            </ListItemButton>
                          </ListItem>
                          {index < conversations.length - 1 && <Divider />}
                        </div>
                      );
                    })}
                  </List>
                )}
              </ArgonBox>
            </Card>

            {/* Right Panel: Messages View */}
            <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              {/* Messages Header */}
              <ArgonBox p={2} borderBottom="1px solid #e0e0e0">
                {selectedConversation ? (
                  <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                    <ArgonBox>
                      <ArgonTypography variant="h6">
                        {selectedConversation.subject || 'Conversation'}
                      </ArgonTypography>
                      <ArgonTypography variant="caption" color="text">
                        State: {selectedConversation.state} • Messages:{' '}
                        {selectedConversation.messageCount || 0}
                      </ArgonTypography>
                    </ArgonBox>
                    {/* Connection Status Indicator */}
                    <Icon
                      fontSize="small"
                      sx={{
                        color: wsConnected ? '#4caf50' : '#f44336',
                        fontSize: '16px'
                      }}
                    >
                      {wsConnected ? 'wifi' : 'wifi_off'}
                    </Icon>
                  </ArgonBox>
                ) : (
                  <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
                    <ArgonTypography variant="h6" color="text">
                      Select a conversation to view messages
                    </ArgonTypography>
                    {/* Connection Status Indicator */}
                    <Icon
                      fontSize="small"
                      sx={{
                        color: wsConnected ? '#4caf50' : '#f44336',
                        fontSize: '16px'
                      }}
                    >
                      {wsConnected ? 'wifi' : 'wifi_off'}
                    </Icon>
                  </ArgonBox>
                )}
              </ArgonBox>

              {/* Messages Area */}
              <ArgonBox
                ref={messageListRef}
                flex={1}
                p={1}
                sx={{
                  overflowY: 'auto',
                  backgroundColor: '#f8f9fa'
                }}>
                {messagesLoading ? (
                  <ArgonBox display="flex" justifyContent="center" p={3}>
                    <ArgonTypography variant="body2" color="text">
                      Loading messages...
                    </ArgonTypography>
                  </ArgonBox>
                ) : !selectedConversationId ? (
                  <ArgonBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="100%">
                    <ArgonTypography
                      variant="body2"
                      color="text"
                      textAlign="center">
                      Select a conversation from the left panel
                      <br />
                      to view and send messages
                    </ArgonTypography>
                  </ArgonBox>
                ) : messages.length === 0 ? (
                  <ArgonBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="100%">
                    <ArgonTypography variant="body2" color="text">
                      No messages in this conversation yet.
                    </ArgonTypography>
                  </ArgonBox>
                ) : (
                  <>
                    <MessageList
                      className="message-list"
                      lockable={true}
                      toBottomHeight="100%"
                      dataSource={messages}
                    />
                    {/* Typing Indicator */}
                    {typingUsers.size > 0 && (
                      <ArgonBox p={1} pl={2}>
                        <ArgonTypography variant="caption" color="text" fontStyle="italic">
                          User is typing...
                        </ArgonTypography>
                      </ArgonBox>
                    )}
                  </>
                )}
              </ArgonBox>

              {/* Message Input Area */}
              {selectedConversationId && (
                <ArgonBox p={2} sx={{ borderTop: '1px solid #e0e0e0' }}>
                  <ArgonBox display="flex" gap={1}>
                    <ArgonBox flex={1}>
                      <Input
                        placeholder="Type your response..."
                        multiline={false}
                        value={newMessage}
                        onChange={(e) => handleTyping(e.target.value)}
                        onKeyPress={handleKeyPress}
                        maxHeight={100}
                        referance={null}
                        clear={() => setNewMessage('')}
                        maxlength={10000}
                      />
                    </ArgonBox>
                    <ArgonButton
                      variant="gradient"
                      color="info"
                      size="small"
                      onClick={() => sendAgentMessage()}
                      disabled={!newMessage.trim() || sendingMessage}
                      sx={{ minWidth: 'auto', px: 2 }}>
                      <Icon>send</Icon>
                    </ArgonButton>
                  </ArgonBox>
                </ArgonBox>
              )}
            </Card>
          </ArgonBox>
        )}
      </ArgonBox>
    </DashboardLayout>
  );
}

export default ChatAgentTest;
